import React, { useState, useEffect, useRef } from 'react';
import { getThemeColors, spacing, typography, borderRadius, getShadowStyle } from '../../utils/theme';
import UserDashboardHeader from '../../components/UserDashboardHeader';

interface ChatRoom {
  id: string;
  name?: string;
  type: 'private' | 'group' | 'chama' | 'support';
  last_message?: string;
  lastMessage?: string;
  last_message_at?: string;
  unread_count?: number;
  unreadCount?: number;
  member_count?: number;
  memberCount?: number;
  participants?: any[];
  otherUserName?: string;
  otherUserAvatar?: string;
  other_user_online?: boolean;
}

const colors = getThemeColors('dark');

const Chat: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('all');
  const [chatRooms, setChatRooms] = useState<ChatRoom[]>([]);
  const [chatServiceAvailable, setChatServiceAvailable] = useState(true);
  const [selectedChat, setSelectedChat] = useState<string | null>(null);
  const [message, setMessage] = useState('');

  // Mock chat rooms data - replicating mobile structure
  const mockChatRooms: ChatRoom[] = [
    {
      id: '1',
      name: 'Tech Innovators Chama',
      type: 'chama',
      last_message: 'Meeting scheduled for tomorrow at 2 PM',
      last_message_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(), // 30 mins ago
      unread_count: 2,
      member_count: 15,
    },
    {
      id: '2',
      name: 'Women Empowerment Group',
      type: 'group',
      last_message: 'Thanks for the contribution! 🎉',
      last_message_at: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(), // 2 hours ago
      unread_count: 0,
      member_count: 8,
    },
    {
      id: '3',
      name: 'Investment Club',
      type: 'group',
      last_message: 'New investment opportunity in tech stocks 📈',
      last_message_at: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(), // 1 day ago
      unread_count: 1,
      member_count: 12,
    },
    {
      id: '4',
      name: 'John Doe',
      type: 'private',
      last_message: 'Hey, are you available for a quick call?',
      last_message_at: new Date(Date.now() - 15 * 60 * 1000).toISOString(), // 15 mins ago
      unread_count: 3,
      otherUserName: 'John Doe',
      other_user_online: true,
    },
    {
      id: '5',
      name: 'Support Chat',
      type: 'support',
      last_message: 'Your support ticket has been resolved ✅',
      last_message_at: new Date(Date.now() - 3 * 60 * 60 * 1000).toISOString(), // 3 hours ago
      unread_count: 0,
      member_count: 2,
    },
  ];

  useEffect(() => {
    loadChatRooms();
  }, []);

  const loadChatRooms = async () => {
    try {
      setLoading(true);
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      setChatRooms(mockChatRooms);
    } catch (error) {
      console.error('Failed to load chat rooms:', error);
      setChatServiceAvailable(false);
    } finally {
      setLoading(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadChatRooms();
    setRefreshing(false);
  };

  const getFilteredRooms = () => {
    let filtered = chatRooms;

    if (activeTab === 'private') {
      filtered = chatRooms.filter(room => room.type === 'private');
    } else if (activeTab === 'groups') {
      filtered = chatRooms.filter(room =>
        room.type === 'group' || room.type === 'chama' || room.type === 'support'
      );
    }

    if (searchQuery) {
      filtered = filtered.filter(room =>
        room.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        room.last_message?.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    return filtered.sort((a, b) =>
      new Date(b.last_message_at || 0).getTime() - new Date(a.last_message_at || 0).getTime()
    );
  };

  const formatTime = (dateString: string) => {
    if (!dateString) return '';

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'now';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  const getChatDisplayName = (room: ChatRoom) => {
    if (room.type === 'private') {
      return room.otherUserName || room.name || `Private Chat ${room.id}`;
    }
    return room.name || `${room.type} ${room.id}`;
  };

  const getUnreadCount = (room: ChatRoom) => {
    return room.unread_count || room.unreadCount || 0;
  };

  const getMemberCount = (room: ChatRoom) => {
    if (room.type === 'private') return 2;
    return room.member_count || room.memberCount || 0;
  };

  const handleChatPress = (room: ChatRoom) => {
    alert(`Navigate to Chat Room: ${getChatDisplayName(room)}\n\nRoom ID: ${room.id}\nType: ${room.type}`);
  };

  const handleCreatePrivateChat = () => {
    alert('Navigate to Create Private Chat page');
  };

  const handleCreateGroupChat = () => {
    alert('Navigate to Create Group Chat page');
  };

  const handleSendMessage = () => {
    if (message.trim()) {
      console.log('Sending message:', message);
      setMessage('');
    }
  };

  const renderHeader = () => (
    <div style={{
      paddingLeft: spacing.md,
      paddingRight: spacing.md,
      paddingTop: spacing.sm,
      paddingBottom: spacing.sm,
      backgroundColor: colors.surface,
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: spacing.xs,
      }}>
        {/* Status Indicator */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          paddingLeft: spacing.xs,
          paddingRight: spacing.xs,
          paddingTop: spacing.xs,
          paddingBottom: spacing.xs,
          borderRadius: borderRadius.full,
          backgroundColor: chatServiceAvailable ? colors.success + '20' : colors.warning + '20',
          gap: 4,
        }}>
          <span style={{
            fontSize: 16,
            color: chatServiceAvailable ? colors.success : colors.warning
          }}>
            {chatServiceAvailable ? '📶' : '📵'}
          </span>
        </div>

        {/* Tabs */}
        <div style={{
          display: 'flex',
          flex: 1,
          gap: spacing.xs,
          marginLeft: spacing.xs,
          marginRight: spacing.xs,
        }}>
          {[
            { id: 'all', name: 'All', count: chatRooms.length },
            { id: 'private', name: 'Private', count: chatRooms.filter(r => r.type === 'private').length },
            { id: 'groups', name: 'Groups', count: chatRooms.filter(r =>
              r.type === 'group' || r.type === 'chama' || r.type === 'support'
            ).length },
          ].map((tab) => (
            <button
              key={tab.id}
              style={{
                flex: 1,
                paddingLeft: spacing.sm,
                paddingRight: spacing.sm,
                paddingTop: spacing.xs,
                paddingBottom: spacing.xs,
                borderRadius: borderRadius.full,
                border: `1px solid ${colors.border}`,
                backgroundColor: activeTab === tab.id ? colors.primary : 'transparent',
                cursor: 'pointer',
                fontSize: typography.fontSize.xs,
                fontWeight: typography.fontWeight.medium,
                color: activeTab === tab.id ? colors.white : colors.textSecondary,
                textAlign: 'center',
                minWidth: 0,
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.name} ({tab.count})
            </button>
          ))}
        </div>

        {/* Search Button */}
        <button
          style={{
            padding: spacing.xs,
            borderRadius: borderRadius.full,
            backgroundColor: 'transparent',
            border: 'none',
            cursor: 'pointer',
          }}
          onClick={() => alert('Navigate to User Search page')}
        >
          <span style={{ fontSize: 20, color: colors.primary }}>🔍</span>
        </button>
      </div>
    </div>
  );

  const renderChatRoom = (room: ChatRoom) => {
    const isGroup = room.type === 'group' || room.type === 'chama' || room.type === 'support';
    const unreadCount = getUnreadCount(room);
    const displayName = getChatDisplayName(room);

    return (
      <div
        key={room.id}
        style={{
          display: 'flex',
          alignItems: 'center',
          padding: spacing.md,
          borderBottom: `1px solid ${colors.border}`,
          cursor: 'pointer',
          backgroundColor: colors.surface,
          transition: 'background-color 0.2s ease',
        }}
        onClick={() => handleChatPress(room)}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = colors.backgroundSecondary;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = colors.surface;
        }}
      >
        <div style={{ position: 'relative', marginRight: spacing.md }}>
          {isGroup ? (
            <div style={{
              width: 50,
              height: 50,
              borderRadius: 25,
              backgroundColor: colors.primary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{ fontSize: 24, color: colors.white }}>👥</span>
            </div>
          ) : (
            <div style={{
              width: 50,
              height: 50,
              borderRadius: 25,
              backgroundColor: colors.secondary,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}>
              <span style={{
                fontSize: typography.fontSize.lg,
                color: colors.white,
                fontWeight: typography.fontWeight.bold
              }}>
                {displayName.charAt(0).toUpperCase()}
              </span>
            </div>
          )}

          {/* Online indicator for private chats */}
          {!isGroup && room.other_user_online && (
            <div style={{
              position: 'absolute',
              bottom: 2,
              right: 2,
              width: 12,
              height: 12,
              borderRadius: 6,
              backgroundColor: colors.success,
              border: `2px solid ${colors.white}`,
            }} />
          )}
        </div>

        <div style={{ flex: 1 }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: spacing.xs,
          }}>
            <span style={{
              fontSize: typography.fontSize.base,
              fontWeight: typography.fontWeight.semibold,
              color: colors.text,
              flex: 1,
            }}>
              {displayName}
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: spacing.sm }}>
              {room.last_message_at && (
                <span style={{
                  fontSize: typography.fontSize.sm,
                  color: colors.textTertiary,
                }}>
                  {formatTime(room.last_message_at)}
                </span>
              )}

              {unreadCount > 0 && (
                <div style={{
                  backgroundColor: colors.primary,
                  color: colors.white,
                  borderRadius: borderRadius.full,
                  minWidth: 20,
                  height: 20,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  paddingLeft: spacing.xs,
                  paddingRight: spacing.xs,
                  fontSize: typography.fontSize.xs,
                  fontWeight: typography.fontWeight.bold,
                }}>
                  {unreadCount > 99 ? '99+' : unreadCount}
                </div>
              )}
            </div>
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <span style={{
              fontSize: typography.fontSize.sm,
              color: colors.textSecondary,
              flex: 1,
              marginRight: spacing.sm,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {room.last_message || room.lastMessage || 'No messages yet'}
            </span>

            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: spacing.xs / 2,
            }}>
              {/* Message Count */}
              {getMemberCount(room) > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs / 2,
                }}>
                  <span style={{ fontSize: 12, color: colors.textTertiary }}>
                    {isGroup ? '👥' : '💬'}
                  </span>
                  <span style={{
                    fontSize: typography.fontSize.xs,
                    color: colors.textTertiary,
                  }}>
                    {isGroup ? `${getMemberCount(room)} members` : '2 members'}
                  </span>
                </div>
              )}

              {/* Online status for private chats */}
              {!isGroup && room.other_user_online !== undefined && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs / 2,
                }}>
                  <div style={{
                    width: 8,
                    height: 8,
                    borderRadius: 4,
                    backgroundColor: room.other_user_online ? colors.success : colors.textTertiary,
                  }} />
                  <span style={{
                    fontSize: typography.fontSize.xs,
                    color: colors.textTertiary,
                  }}>
                    {room.other_user_online ? 'Online' : 'Offline'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEmptyState = () => (
    <div style={{
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      paddingTop: spacing.xxxl,
      paddingBottom: spacing.xxxl,
      paddingLeft: spacing.xl,
      paddingRight: spacing.xl,
    }}>
      <span style={{ fontSize: 64, marginBottom: spacing.lg, opacity: 0.5 }}>
        💬
      </span>
      <h2 style={{
        fontSize: typography.fontSize.xl,
        fontWeight: typography.fontWeight.semibold,
        color: colors.text,
        marginBottom: spacing.sm,
        textAlign: 'center',
      }}>
        No conversations yet
      </h2>
      <p style={{
        fontSize: typography.fontSize.base,
        color: colors.textSecondary,
        textAlign: 'center',
        lineHeight: 1.5,
        maxWidth: 300,
        marginBottom: spacing.xl,
      }}>
        Start a conversation with someone or create a group chat
      </p>

      <div style={{
        width: '100%',
        gap: spacing.md,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <button
          style={{
            padding: spacing.md,
            backgroundColor: colors.primary,
            color: colors.white,
            border: 'none',
            borderRadius: borderRadius.md,
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.semibold,
            cursor: 'pointer',
          }}
          onClick={handleCreatePrivateChat}
        >
          <span style={{ marginRight: spacing.sm }}>👤</span>
          Start Private Chat
        </button>

        <button
          style={{
            padding: spacing.md,
            backgroundColor: 'transparent',
            color: colors.primary,
            border: `1px solid ${colors.primary}`,
            borderRadius: borderRadius.md,
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.semibold,
            cursor: 'pointer',
          }}
          onClick={handleCreateGroupChat}
        >
          <span style={{ marginRight: spacing.sm }}>👥</span>
          Create Group
        </button>
      </div>
    </div>
  );

  const styles: { [key: string]: React.CSSProperties } = {
    container: {
      minHeight: '100vh',
      backgroundColor: colors.background,
      paddingBottom: 80, // Space for tab bar
    },
    scrollableContent: {
      flex: 1,
      overflowY: 'auto',
      paddingTop: 80, // Space for fixed header
      paddingBottom: 80, // Space for fixed tab bar
    },
    content: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: spacing.md,
    },
    chatContainer: {
      maxWidth: '800px',
      margin: '0 auto',
      backgroundColor: colors.surface,
      borderRadius: borderRadius.lg,
      ...getShadowStyle('md'),
      overflow: 'hidden',
      minHeight: 'calc(100vh - 160px)', // Account for header and tab bar
    },
    chatList: {
      flex: 1,
      overflowY: 'auto',
    },
    loadingContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      padding: spacing.xxxl,
    },
  };

  return (
    <div style={styles.container}>
      <UserDashboardHeader
        title="Messages"
        showBackButton={true}
        showHomeButton={true}
      />

      <div style={styles.scrollableContent}>
        <div style={styles.content}>
          <div style={styles.chatContainer}>
            {renderHeader()}

            <div style={styles.chatList}>
              {loading ? (
                <div style={styles.loadingContainer}>
                  <span style={{ fontSize: typography.fontSize.lg, color: colors.textSecondary }}>
                    Loading chats...
                  </span>
                </div>
              ) : getFilteredRooms().length > 0 ? (
                getFilteredRooms().map(renderChatRoom)
              ) : (
                renderEmptyState()
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;